import { useMemo } from 'react'
import { publicApi } from '@app/store/api/public'
import { useLocation, useParams } from 'react-router-dom'
import { ContentPageType } from '@app/store/api/customer.types'
import { useAppSelector } from '@app/store/hooks'
import { flatten } from 'flat'
import urls from '@app/routes/urls'
import { getContentTypeFromPath } from './utils'
import useStaticContent from './useStaticContent'

/**
 * Hook that returns campaign data from the API with persistent message loading during language changes
 * @param enabled - Boolean determining whether to execute the hook
 * @returns The localised messages and status of the campaign
 */
const useDynamicCampaign = (enabled: boolean) => {
  const location = useLocation()
  const params = useParams<{ program: string; campaign?: string }>()
  const { content: staticContent, loaded: staticContentLoaded, isInitialLoad } = useStaticContent()
  const { language } = useAppSelector(state => state.settings)

  const pageType = useMemo(
    () => getContentTypeFromPath(params.program ?? '', params.campaign ?? '', location.pathname),
    [params.program, params.campaign, location.pathname]
  )

  const shouldUseStaticContent = useMemo(() => {
    const staticPages = [urls.cookies]
    const lastPathPart = location.pathname.split('/').filter(Boolean).pop()
    return staticPages.includes(lastPathPart ?? '')
  }, [location.pathname])

  const {
    data: metadata,
    error: metadataError,
    isFetching: metadataIsFetching,
    isSuccess: metadataIsSuccess,
  } = publicApi.useGetCampaignDetailsQuery(
    { programme_slug: params.program ?? '', campaign_code: params.campaign ?? '' },
    { skip: !params.program || !params.campaign || !enabled }
  )

  const metadataNotFound = useMemo(() => {
    return metadataError && 'status' in metadataError && metadataError?.status === 404
  }, [metadataError])

  const metadataGone = useMemo(() => {
    return metadataError && 'status' in metadataError && metadataError?.status === 410
  }, [metadataError])

  // Query the API for campaign content
  const {
    data: content,
    error: contentError,
    isSuccess: contentSuccess,
  } = publicApi.useGetCampaignContentQuery(
    {
      programme_slug: params.program ?? '',
      campaign_code: params.campaign ?? '',
      page_type: pageType as ContentPageType,
      language: language.toUpperCase(),
    },
    {
      skip:
        !params.program ||
        !params.campaign ||
        !pageType ||
        !language ||
        !enabled ||
        metadataIsFetching ||
        metadataNotFound,
    }
  )

  // Get the FAQ Banner content
  const { data: faqBannerContent } = publicApi.useGetCampaignContentQuery(
    {
      programme_slug: params.program ?? '',
      campaign_code: params.campaign ?? '',
      page_type: ContentPageType.PARTIAL_FAQ_BANNER,
      language: language.toUpperCase(),
    },
    { skip: !params.program || !params.campaign || !language || !enabled }
  )

  // Get the Quiz Tracker content
  const { data: quizTrackerContent } = publicApi.useGetCampaignContentQuery(
    {
      programme_slug: params.program ?? '',
      campaign_code: params.campaign ?? '',
      page_type: ContentPageType.PARTIAL_QUIZ_TRACKER,
      language: language.toUpperCase(),
    },
    { skip: !params.program || !params.campaign || !language || !enabled || !metadataIsSuccess }
  )

  const messages = useMemo(() => {
    if (!enabled) {
      return {}
    }

    if (content && pageType && faqBannerContent) {
      const dynamic: Record<string, string> = flatten({ [pageType]: content })
      const faqBanner: Record<string, string> = flatten({ [ContentPageType.PARTIAL_FAQ_BANNER]: faqBannerContent })
      const quizTracker: Record<string, string> = flatten({
        [ContentPageType.PARTIAL_QUIZ_TRACKER]: quizTrackerContent,
      })

      // Merge the local content with the dynamic data
      return { ...staticContent, ...dynamic, ...faqBanner, ...quizTracker }
    }

    return staticContent
  }, [content, faqBannerContent, quizTrackerContent, pageType, staticContent, enabled])

  const campaignNotFound = useMemo(() => {
    if (!enabled) {
      return false
    }
    return metadataNotFound
  }, [enabled, metadataNotFound])

  const campaignEnded = useMemo(() => {
    if (!enabled) {
      return false
    }

    if (metadataGone) {
      return true
    }

    return metadata?.end_date && new Date(metadata.end_date) < new Date()
  }, [enabled, metadata, metadataGone])

  const messagesReady = useMemo(() => {
    if (shouldUseStaticContent) {
      return staticContentLoaded && Object.keys(staticContent).length > 0
    }

    const hasCompleteMessages = staticContentLoaded && Object.keys(messages).length > 0
    return hasCompleteMessages && (contentSuccess || contentError)
  }, [shouldUseStaticContent, staticContentLoaded, contentSuccess, contentError, staticContent, messages])

  // Do not execute the hook if it is not enabled
  if (!enabled) {
    return {
      messages: {},
      messagesLoaded: false,
      notFound: false,
      ended: false,
      isInitialLoad: true,
    }
  }

  return {
    messages,
    messagesLoaded: messagesReady,
    notFound: campaignNotFound,
    ended: campaignEnded,
    isInitialLoad,
  }
}

export default useDynamicCampaign
